import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, Upload } from "lucide-react";
import { useConfig, ALL_CONFIG_GROUPS } from "@/hooks/useConfig";

export default function ProfilePage() {
  const { user } = useAuth();
  const { getValues } = useConfig(ALL_CONFIG_GROUPS);
  const DEPARTMENTS = getValues("departments");
  const [form, setForm] = useState({ name: "", phone: "", department: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({ name: user.name || "", phone: user.phone || "", department: user.department || "" });
    }
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/users/${user._id}`, { name: form.name, phone: form.phone, department: form.department });
      toast.success("Profile updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("File too large. Max 2MB."); return; }
    if (!["image/jpeg", "image/png"].includes(file.type)) { toast.error("Only JPG/PNG allowed."); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("entity_type", "user");
      formData.append("entity_id", user._id);
      formData.append("document_type", "profile_photo");
      const { data } = await api.post("/documents/upload", formData);
      await api.put(`/users/${user._id}`, { profile_photo: data.file_url });
      toast.success("Profile photo updated. Reload page to see changes.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  if (!user) return null;

  return (
    <div data-testid="profile-page" className="max-w-lg">
      <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950 mb-1">My Profile</h1>
      <p className="text-sm text-zinc-500 mb-6">Manage your personal information</p>

      <div className="border border-zinc-200 p-6 space-y-4">
        {/* Avatar */}
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 rounded-full bg-zinc-900 text-white flex items-center justify-center text-2xl font-bold" data-testid="profile-avatar">
            {user.profile_photo ? (
              <img src={user.profile_photo} alt="Profile" className="w-16 h-16 rounded-full object-cover" />
            ) : (
              user.name?.charAt(0)?.toUpperCase() || "U"
            )}
          </div>
          <div>
            <Label className="text-xs cursor-pointer">
              <span className="inline-flex items-center gap-1 px-3 py-1.5 border border-zinc-200 rounded-sm text-xs hover:bg-zinc-50 transition-colors">
                <Upload size={12} /> {uploading ? "Uploading..." : "Upload Photo"}
              </span>
              <Input type="file" accept=".jpg,.jpeg,.png" onChange={handlePhotoUpload} className="hidden" data-testid="profile-photo-upload" />
            </Label>
            <p className="text-[10px] text-zinc-400 mt-1">JPG/PNG only, max 2MB</p>
          </div>
        </div>

        <div>
          <Label className="text-xs">Full Name</Label>
          <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" data-testid="profile-name" />
        </div>
        <div>
          <Label className="text-xs">Email</Label>
          <Input value={user.email} readOnly className="mt-1 rounded-sm bg-zinc-50 text-zinc-500" data-testid="profile-email" />
        </div>
        <div>
          <Label className="text-xs">Role</Label>
          <Input value={(user.roles || []).join(", ")} readOnly className="mt-1 rounded-sm bg-zinc-50 text-zinc-500" data-testid="profile-role" />
        </div>
        <div>
          <Label className="text-xs">Department</Label>
          <Select value={form.department || "none"} onValueChange={v => setForm({ ...form, department: v === "none" ? "" : v })}>
            <SelectTrigger className="mt-1 rounded-sm" data-testid="profile-department"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent><SelectItem value="none">-- Select --</SelectItem>{DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Phone Number</Label>
          <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-sm" data-testid="profile-phone" />
        </div>

        <Button onClick={handleSave} disabled={saving} className="rounded-sm text-xs" data-testid="profile-save">
          <Save size={14} /> {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
}
